import { AuthenticationGuard } from "@/modules/auth/components/authentication-guard";
import { IframeAuthenticationGuard } from "@/modules/auth/components/iframe-authentication-guard";
import { SidebarLayout } from "@/modules/customer-service/components/sidebar-layout";
import { IframeSimpleLayout } from "@/modules/layout/components/iframe-simple-layout";
import { MainLayout } from "@/modules/layout/components/main-layout";
import { PrintLayout } from "@/modules/layout/components/print-layout";
import { routePaths } from "@/router/route-paths";

import { AbilityRoute } from "./ability-route";
import { BaseRoute } from "./base-route";
import { DeveloperRoute } from "./developer-route";
import { lazyRoute } from "./lazy-route";

const Screens = {
  Procedures: () => import("@/modules/procedures/screens/procedures-screen"),
  ProceduresViewsIndex: () =>
    import("@/modules/procedures/screens/procedures-views-index-screen"),
  Procedure: () => import("@/modules/procedures/screens/procedure-screen"),
  ProcedureData: () =>
    import("@/modules/procedures/screens/procedure-data-screen"),
  ProcedureSteps: () =>
    import("@/modules/procedures/screens/procedure-steps-screen"),
  NewProcedure: () =>
    import("@/modules/procedures/screens/new-procedure-screen"),

  Customers: () => import("@/modules/customers/screens/customers-screen"),
  NewCustomer: () => import("@/modules/customers/screens/new-customer-screen"),
  Customer: () => import("@/modules/customers/screens/customer-detail-screen"),
  CustomerData: () =>
    import("@/modules/customers/screens/customer-data-screen"),
  CustomerUsers: () =>
    import("@/modules/customers/screens/customer-users-screen"),
  CustomerCustomers: () =>
    import("@/modules/customers/screens/customer-customers-screen"),
  CustomerSettings: () =>
    import("@/modules/customers/screens/customer-settings-screen"),
  Settings: () => import("@/modules/dummy/screens/settings-screen"),
  Export: () => import("@/modules/export/screens/export-screen"),
  EnergySavingCertificates: () =>
    import("@/modules/energy-saving-certificates/screens/energy-saving-certificates-screen"),
  NewEnergySavingCertificate: () =>
    import("@/modules/energy-saving-certificates/screens/new-energy-saving-certificate-screen"),
  NewTRA020Certificate: () =>
    import("@/modules/energy-saving-certificates/screens/new-tra020-certificate-screen"),
  NewCertificateDetail: () =>
    import("@/modules/energy-saving-certificates/screens/new-certificate-detail-screen"),
  EnergySavingCertificate: () =>
    import("@/modules/energy-saving-certificates/screens/energy-saving-certificate-screen"),
  EnergySavingCertificateData: () =>
    import("@/modules/energy-saving-certificates/screens/energy-saving-certificate-data-screen"),
  EnergySavingCertificateVehicles: () =>
    import("@/modules/energy-saving-certificates/screens/energy-saving-certificate-vehicles-screen"),
  EnergySavingCertificateSteps: () =>
    import("@/modules/energy-saving-certificates/screens/energy-saving-certificate-steps-screen"),
  ProcedureBatches: () =>
    import("@/modules/procedures/screens/procedure-batches-screen"),
  NewProcedureBatch: () =>
    import("@/modules/procedures/screens/new-procedure-batch-screen"),
  ProcedureBatch: () =>
    import("@/modules/procedures/screens/procedure-batch-screen"),
  ProcedureBatchParticipants: () =>
    import("@/modules/procedures/screens/procedure-batch-participants-screen"),
  ProcedureBatchProcedures: () =>
    import("@/modules/procedures/screens/procedure-batch-procedures-screen"),
  ProcedureBatchDocuments: () =>
    import("@/modules/procedures/screens/procedure-batch-documents-screen"),

  // Reports
  Reports: () => import("@/modules/reports/screens/reports-screen"),
  ReportRedirect: () =>
    import("@/modules/reports/screens/report-redirect-screen"),

  // Tax liquidation methods
  TaxLiquidationMethods: () =>
    import("@/modules/tax-liquidation-methods/screens/tax-liquidation-methods-screen"),
  TaxLiquidationMethodDetail: () =>
    import("@/modules/tax-liquidation-methods/screens/tax-liquidation-method-detail-screen"),
  TaxLiquidationMethodCreate: () =>
    import("@/modules/tax-liquidation-methods/screens/tax-liquidation-method-create-screen"),

  // Signatures
  Signatures: () => import("@/modules/signatures/screens/signatures-screen"),
  SignatureCreate: () =>
    import("@/modules/signatures/screens/signature-create-screen"),
  SignatureEdit: () =>
    import("@/modules/signatures/screens/signature-edit-screen"),

  // Messages
  Inbox: () => import("@/modules/inbox/screens/inbox-screen"),

  // Customer service
  CustomerServiceSidebar: () =>
    import("@/modules/customer-service/screens/sidebar-screen"),

  // Deliveries
  DeliveryNote: () =>
    import("@/modules/deliveries/screens/delivery-note-screen"),
  NewDelivery: () =>
    import("@/modules/deliveries/screens/create-delivery-screen"),

  // Commander (developer tool)
  Commander: () => import("@/modules/commander/screens/commander-screen"),
};

export const authenticatedRoutes = [
  {
    element: <AuthenticationGuard component={MainLayout} />,
    children: [
      {
        path: routePaths.base,
        element: <BaseRoute />,
      },
      {
        path: routePaths.procedures,
        lazy: lazyRoute(Screens.Procedures),
      },
      {
        path: routePaths.procedureViewsIndex,
        lazy: lazyRoute(Screens.ProceduresViewsIndex),
      },
      {
        path: routePaths.procedureViewsRoutePattern,
        lazy: lazyRoute(Screens.Procedures),
      },
      {
        path: routePaths.energySavingCertificates,
        lazy: lazyRoute(Screens.EnergySavingCertificates),
      },
      {
        path: routePaths.newEnergySavingCertificate,
        lazy: lazyRoute(Screens.NewEnergySavingCertificate),
      },
      {
        path: routePaths.newEnergySavingCertificateTRA020,
        lazy: lazyRoute(Screens.NewTRA020Certificate),
      },
      {
        path: routePaths.newEnergySavingCertificateWithId,
        lazy: lazyRoute(Screens.NewCertificateDetail),
      },
      {
        path: routePaths.energySavingCertificate,
        lazy: lazyRoute(Screens.EnergySavingCertificate),
        children: [
          {
            index: true,
            lazy: lazyRoute(Screens.EnergySavingCertificateData),
          },
          {
            path: routePaths.energySavingCertificateVehicles,
            lazy: lazyRoute(Screens.EnergySavingCertificateVehicles),
          },
          {
            path: routePaths.energySavingCertificateSteps,
            lazy: lazyRoute(Screens.EnergySavingCertificateSteps),
          },
        ],
      },
      {
        path: routePaths.export,
        lazy: lazyRoute(Screens.Export),
      },
      {
        path: routePaths.newProcedure,
        lazy: lazyRoute(Screens.NewProcedure),
      },
      {
        path: routePaths.newDelivery,
        lazy: lazyRoute(Screens.NewDelivery),
      },
      {
        path: routePaths.procedure,
        lazy: lazyRoute(Screens.Procedure),
        children: [
          {
            index: true,
            lazy: lazyRoute(Screens.ProcedureData),
          },
          {
            path: routePaths.procedureSteps,
            lazy: lazyRoute(Screens.ProcedureSteps),
          },
        ],
      },

      {
        path: routePaths.customers,
        lazy: lazyRoute(Screens.Customers),
      },
      {
        path: routePaths.newCustomer,
        lazy: lazyRoute(Screens.NewCustomer),
      },
      {
        path: routePaths.newCustomerWithId,
        lazy: lazyRoute(Screens.NewCustomer),
      },
      {
        path: routePaths.customer,
        lazy: lazyRoute(Screens.Customer),
        children: [
          {
            index: true,
            lazy: lazyRoute(Screens.CustomerData),
          },
          {
            path: routePaths.customerUsers,
            lazy: lazyRoute(Screens.CustomerUsers),
          },
          {
            path: routePaths.customerCustomers,
            lazy: lazyRoute(Screens.CustomerCustomers),
          },
          {
            path: routePaths.customerSettings,
            lazy: lazyRoute(Screens.CustomerSettings),
          },
        ],
      },
      {
        path: routePaths.settings,
        lazy: lazyRoute(Screens.Settings),
      },
      {
        element: <DeveloperRoute />,
        children: [
          {
            path: routePaths.commander,
            lazy: lazyRoute(Screens.Commander),
          },
        ],
      },

      {
        path: routePaths.taxLiquidationMethods,
        lazy: lazyRoute(Screens.TaxLiquidationMethods),
      },
      {
        path: routePaths.newTaxLiquidationMethod,
        lazy: lazyRoute(Screens.TaxLiquidationMethodCreate),
      },
      {
        path: routePaths.taxLiquidationMethod,
        lazy: lazyRoute(Screens.TaxLiquidationMethodDetail),
      },

      {
        path: routePaths.batches,
        lazy: lazyRoute(Screens.ProcedureBatches),
      },
      {
        path: routePaths.newBatch,
        lazy: lazyRoute(Screens.NewProcedureBatch),
      },
      {
        path: routePaths.reports,
        lazy: lazyRoute(Screens.Reports),
      },
      {
        path: routePaths.report,
        lazy: lazyRoute(Screens.ReportRedirect),
      },
      {
        element: <AbilityRoute subject="SignatureRequest" />,
        children: [
          { path: routePaths.signatures, lazy: lazyRoute(Screens.Signatures) },
          {
            path: routePaths.newSignature,
            lazy: lazyRoute(Screens.SignatureCreate),
          },
          {
            path: routePaths.editSignature,
            lazy: lazyRoute(Screens.SignatureEdit),
          },
        ],
      },
      {
        // ONE entry for both `/messages` and `/messages/:conversationId`, which
        // is what the optional parameter buys: the screen holds its filters and
        // its search in local state, and two sibling entries would put it at a
        // different route on either side of the first selection — remounting it
        // and resetting both. The selection has to survive the same way, so the
        // screen stays mounted across it.
        //
        // The parameter is ALWAYS a conversation id: clicking a card selects the
        // group's most recent conversation, so a selection is linkable whichever
        // shape the right pane takes.
        path: routePaths.messagesRoutePattern,
        lazy: lazyRoute(Screens.Inbox),
      },
      {
        path: routePaths.procedureBatch,
        lazy: lazyRoute(Screens.ProcedureBatch),
        children: [
          {
            index: true,
            lazy: lazyRoute(Screens.ProcedureBatchParticipants),
          },
          {
            path: routePaths.procedureBatchProcedures,
            lazy: lazyRoute(Screens.ProcedureBatchProcedures),
          },
          {
            path: routePaths.procedureBatchDocuments,
            lazy: lazyRoute(Screens.ProcedureBatchDocuments),
          },
        ],
      },
    ],
  },
  {
    element: <AuthenticationGuard component={PrintLayout} />,
    children: [
      {
        element: <AbilityRoute subject="DeliveryTracking" />,
        children: [
          {
            path: routePaths.deliveryNote,
            lazy: lazyRoute(Screens.DeliveryNote),
          },
        ],
      },
    ],
  },
  {
    element: <IframeAuthenticationGuard component={IframeSimpleLayout} />,
    children: [
      {
        path: routePaths.customerService,
        element: <SidebarLayout />,
        children: [
          {
            index: true,
            lazy: lazyRoute(Screens.CustomerServiceSidebar),
          },
        ],
      },
    ],
  },
];
